import { GetStaticPaths, GetStaticProps } from "next";
import Head from "next/head";
import { useState } from "react";
import { Seomatic } from "react-seomatic";

import PageSections from "@components/PageSections";
import PasswordPageLayout from "@components/PasswordPageLayout";
import useSetHeaderTheme from "@hooks/useSetHeaderTheme";
import { PreviewContent } from "@interfaces/PreviewContent.interface";
import craft from "@pages/api/craft";
import { returnStaticProps } from "@lib/returnStaticProps";
import getGridEntries from "@lib/getGridEntries";
import getEmbeddedForms from "@lib/getEmbeddedForms";
import QueryPage from "@queries/QueryPage.graphql";
import Script from "next/script";
import isPage from "@lib/isPage";
import { PHASE_PRODUCTION_BUILD } from "next/constants";

export const getStaticPaths: GetStaticPaths = async () => {
	// Fetch every possible page path
	// const pages = await craft(QuerySlugs, { section: ["pages", "landingPages"], limit: null });

	if (process.env.NEXT_PHASE !== PHASE_PRODUCTION_BUILD) {
		await isPage.getRedirects();
	}

	const pages = await isPage.fetchAndSetPages();

	return {
		paths: pages.map((page) => ({
			params: {
				slug: page.uri.split("/"),
			},
		})),
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async ({
	params,
	previewData: nextPreviewData,
	preview,
}) => {
	// For Craft preview capability
	const previewData: PreviewContent = nextPreviewData as PreviewContent;
	const uri = (params.slug as string[]).join("/");
	console.log(`Preview mode enabled: ${preview}`);

	if (!preview) {
		console.log(`Getting page cache for: ${uri}`);
		const page = await isPage.cache.getPages(uri as string);

		if (!page) {
			const redirect = await isPage.cache.getRedirects(uri);
			//console.log(redirect)
			if (redirect) {
				return {
					redirect: {
						destination: redirect.redirectDestUrl,
						permanent: redirect.redirectHttpCode === 301,
					},
				};
			} else {
				return {
					notFound: true,
				};
			}
		}
	}

	// Fetch the page's data
	const response = await craft(
		QueryPage,
		{ uri },
		preview ? previewData.token : null
	);

	const {
		pageEntry,
		landingPageEntry,
		primaryNavigation,
		primaryCallToActions,
		globalSet,
	} = response;

	let returnResponse = null;

	if (!!response.landingPageEntry) {
		const gridPosts = await getGridEntries(
			response.landingPageEntry?.allPageSections
		);
		const forms = await getEmbeddedForms(
			response.landingPageEntry?.allPageSections
		);
		// Return props if entry exists, otherwise check Retour for redirects or 404
		returnResponse = await returnStaticProps(
			{
				pageEntry,
				landingPageEntry,
				gridPosts,
				forms,
				globalSet,
			},
			!pageEntry && !landingPageEntry,
			uri
		);
	} else {
		// Return props if entry exists, otherwise check Retour for redirects or 404
		const gridPosts = await getGridEntries(response.pageEntry?.allPageSections);
		const forms = await getEmbeddedForms(response.pageEntry?.allPageSections);

		returnResponse = await returnStaticProps(
			{
				navigation: {
					primaryNavigation,
					primaryCallToActions,
				},
				pageEntry,
				landingPageEntry,
				gridPosts,
				globalSet,
				forms,
			},
			!pageEntry && !landingPageEntry,
			uri
		);
	}

	return returnResponse;
};
interface IProps {
	pageEntry: any;
	landingPageEntry: any;
	gridPosts: any;
	forms: any;
}

export default function Page({
	pageEntry,
	landingPageEntry,
	gridPosts,
	forms,
}: IProps): JSX.Element {
	let entry = null;
	if (pageEntry == null && landingPageEntry != null) {
		entry = landingPageEntry;
	} else {
		entry = pageEntry;
	}

	useSetHeaderTheme(entry.headerTheme);

	// Check if password protected
	const isPasswordProtected = !!entry.encryptedPassword;

	const [accessGranted, setAccessGranted] = useState(false);

	let faqs = [];
	let faqSchema = {};

	if (entry.faqSchema?.length) {
		entry.faqSchema.map((item) => {
			faqs.push({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: {
					"@type": "Answer",
					text: item.answer,
				},
			});
		});

		faqSchema = {
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faqs,
		};
	}

	//console.log(gridPosts)

	return (
		<>
			<Seomatic Head={Head} {...entry.seomatic} />
			{entry.faqSchema?.length > 0 && (
				<Script
					type="application/ld+json"
					id="faqSchema"
					dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
				/>
			)}
			{!!entry.pageSections && isPasswordProtected && !accessGranted && (
				<PageSections
					sections={[entry.pageSections[0]]}
					gridPosts={gridPosts}
					forms={forms}
				/>
			)}
			{!!entry.pageSections &&
				((isPasswordProtected && accessGranted) || !isPasswordProtected) && (
					<PageSections
						sections={entry.pageSections}
						gridPosts={gridPosts}
						forms={forms}
					/>
				)}
			{isPasswordProtected && !accessGranted && (
				<PasswordPageLayout
					contentSections={entry.contentSections}
					encryptedPassword={entry.encryptedPassword}
					onFormSuccess={() => setAccessGranted(true)}
				/>
			)}
		</>
	);
}
